import { ref, computed, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMenuStore } from '~/stores/useMenuStore';
import { useNavigateToAsis } from '~/composables/useNavigateToAsis';
import { resolveMenuTabLabel, normalizeMenuRoutePath } from '~/config/tabMenuConfig';
import { appT } from '~/composables/useAppI18n';
import { withAppLocaleQuery } from '~/composables/useAppLocale';

export interface TabMenuItem {
  id: number;
  labelKey?: string;
  label?: string;
  key?: string;
  component?: string;
  path?: string;
}

const resolveTabLabel = (tab: TabMenuItem | undefined) => {
  if (!tab) return '';
  return resolveMenuTabLabel(tab, appT);
};

export interface MenuItemWithTabs {
  name: string;
  path: string;
  pgmId: string;
  tabs?: TabMenuItem[];
  defaultTab?: number;
  /** 'A' = ASIS(레거시 iframe), 'T' = TOBE(Vue 페이지) */
  viewType?: 'A' | 'T';
  [key: string]: any;
}

// 싱글톤으로 공유되는 상태 (함수 외부에 선언)
const activeTab = ref<number>(0);
const currentMenuPgmId = ref('');
// 탭 변경 의도 저장 (pgmId 변경 시에도 탭 인덱스 유지용)
const pendingTabChange = ref<{ pgmId: string; tabIndex: number } | null>(null);
// 사용자가 탭을 클릭 중인지 (initializeTab 리셋 방지용)
let isUserTabChange = false;
// 첫 탭 보정 이동이 현재 네비게이션 setup 안에서 바로 실행되면 화면 마운트가 취소된다.
let scheduledTabRedirect = '';

const scheduleFirstTabRedirect = (
  router: ReturnType<typeof useRouter>,
  targetPath: string,
) => {
  if (!targetPath || scheduledTabRedirect === targetPath) return;
  scheduledTabRedirect = targetPath;
  const query = withAppLocaleQuery({});

  nextTick(() => {
    scheduledTabRedirect = '';
    const currentPath = normalizeMenuRoutePath(router.currentRoute.value.path);
    if (currentPath === targetPath) return;

    router
      .replace({
        path: targetPath,
        query,
      })
      .catch(() => {});
  });
};

export const useTabNavigation = () => {
  const route = useRoute();
  const router = useRouter();
  const { navigateToAsis } = useNavigateToAsis();
  const menuStore = useMenuStore();
  const menuSections = computed(() => menuStore.sections);

  // 경로에 해당하는 메뉴 아이템 찾기 (pathOverride 없으면 현재 라우트 기준)
  const getCurrentMenuItem = (pathOverride?: string): MenuItemWithTabs | null => {
    const currentPath = normalizeMenuRoutePath(pathOverride ?? route.path);
    let bestMatch: MenuItemWithTabs | null = null;
    let maxMatchLength = 0;

    // 모든 메뉴 아이템 수집
    const allMenuItems: MenuItemWithTabs[] = [];

    for (const section of menuSections.value) {
      if (section.items) {
        allMenuItems.push(...section.items);
      }

      if (section.submenus) {
        for (const submenu of section.submenus) {
          if (submenu.items) {
            allMenuItems.push(...submenu.items);
          }
        }
      }
    }

    // 메인 경로 매칭 및 탭 경로 매칭 확인
    for (const item of allMenuItems) {
      const itemPath = normalizeMenuRoutePath(item.path || '');
      // 메인 경로와 일치하는지 확인
      if (itemPath && (currentPath === itemPath || currentPath.startsWith(`${itemPath}/`))) {
        if (itemPath.length > maxMatchLength) {
          maxMatchLength = itemPath.length;
          bestMatch = item;
        }
      }

      // 탭이 있는 메뉴 아이템의 경우, 탭 경로들도 확인
      if (item.tabs && item.tabs.length > 0) {
        for (const tab of item.tabs) {
          if (tab.path) {
            if (currentPath === normalizeMenuRoutePath(tab.path)) {
              // 탭 경로와 정확히 일치하면 해당 메뉴 아이템을 반환
              return item;
            }
          }
        }
      }
    }

    return bestMatch;
  };

  // 현재 메뉴의 탭 목록
  const currentTabs = computed(() => {
    const menuItem = getCurrentMenuItem();
    return menuItem?.tabs || [];
  });

  // 현재 메뉴의 pgmId
  const currentPgmId = computed(() => {
    const menuItem = getCurrentMenuItem();
    return menuItem?.pgmId || '';
  });

  // URL 쿼리에서 탭 정보 파싱
  const parseTabFromQuery = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tab = urlParams.get('tab');
    if (tab) {
      const tabIndex = parseInt(tab, 10);
      if (!isNaN(tabIndex)) {
        activeTab.value = tabIndex;
      }
    }
  };

  // 쿼리 파라미터 업데이트 (비활성화 - URL에 tab 파라미터 추가하지 않음)
  const updateQuery = (tabId: number) => {
    // URL에 tab 파라미터를 추가하지 않음
    // const url = new URL(window.location.href)
    // url.searchParams.set('tab', tabId.toString())
    // window.history.replaceState(null, '', url.toString())
  };

  // 탭 변경 핸들러
  const handleTabChange = (tabId: number) => {
    console.log('[useTabNavigation] 🟡 handleTabChange 호출:', {
      tabId,
      currentActiveTab: activeTab.value,
    });

    // 사용자 탭 변경 플래그 설정 (initializeTab 리셋 방지)
    isUserTabChange = true;
    setTimeout(() => {
      isUserTabChange = false;
    }, 500);

    const selectedTab = currentTabs.value[tabId];
    const currentPgm = currentPgmId.value;
    const currentMenuItem = getCurrentMenuItem();

    console.log('[useTabNavigation] 🟡 selectedTab:', selectedTab, 'currentPgm:', currentPgm);
    console.log(
      '[useTabNavigation] 🟡 selectedTab.path:',
      selectedTab?.path,
      'route.path:',
      route.path,
    );

    // 탭 변경 의도 저장 (SimpleIframeRenderer에서 pgmId watch 시 참조)
    pendingTabChange.value = { pgmId: currentPgm, tabIndex: tabId };

    activeTab.value = tabId;
    console.log('🟡 activeTab 설정 후:', activeTab.value);

    // KPI > Cascading
    if (
      currentMenuItem?.path === '/performance/kpi' &&
      selectedTab?.labelKey === 'menuTab.cascading'
    ) {
      navigateToAsis({
        entryPath: '/main.do?redirectPgmTabIndex=1',
        hash: 'PGM1709',
      });
      return;
    }

    // 업무 > Timeline
    if (currentMenuItem?.path === '/task/home' && selectedTab?.labelKey === 'menuTab.timeline') {
      navigateToAsis({
        entryPath: '/main.do?redirectPgmTabIndex=2',
        hash: 'PGM1710',
      });
      return;
    }

    // 업무 > 요약
    if (currentMenuItem?.path === '/task/home' && selectedTab?.labelKey === 'menuTab.summary') {
      navigateToAsis({
        entryPath: '/main.do?redirectPgmTabIndex=3',
        hash: 'PGM1710',
      });
      return;
    }

    // 탭에 path가 있으면 해당 경로로 이동
    if (selectedTab?.path) {
      const currentPath = normalizeMenuRoutePath(route.path);
      const targetPath = normalizeMenuRoutePath(selectedTab.path);

      if (currentPath === targetPath) {
        // 같은 경로인 경우 contentReload 이벤트 발생 (해당 탭 내용만 새로고침)
        console.log('[useTabNavigation] 🟢 같은 경로 - contentReload 발생');
        window.dispatchEvent(
          new CustomEvent('contentReload', {
            detail: { tabId, tabLabel: resolveTabLabel(selectedTab) },
          }),
        );
      } else {
        // 다른 경로인 경우 바로 이동
        console.log('[useTabNavigation] 🔴 다른 경로 - router.push 실행:', targetPath);
        router.push(targetPath);

        // 이동할 탭이 A타입(ASIS) 메뉴면 layouts/default.vue가 AsisRenderer로 대체 렌더링하여
        // 목적 페이지(index.vue)의 onMounted 로그 호출이 아예 실행되지 않는다 → 여기서 직접 기록
        const targetMenuItem = getCurrentMenuItem(targetPath);
        if (targetMenuItem?.viewType === 'A') {
          logTabMenuAccess(tabId, targetMenuItem.pgmId || currentPgm);
        }
      }
    } else {
      console.log('[useTabNavigation] 🟠 path 없음 - contentReload 발생');
      // path가 없으면 현재 페이지에서 쿼리 파라미터 업데이트하지 않음
      // updateQuery(tabId)

      // 페이지 새로고침 없이 콘텐츠 영역 다시 렌더링
      window.dispatchEvent(
        new CustomEvent('contentReload', {
          detail: { tabId, tabLabel: resolveTabLabel(selectedTab) },
        }),
      );

      // iframe 내부에도 탭 변경 알림
      sendTabChangeToIframes(tabId, resolveTabLabel(selectedTab), currentPgm);
    }

    // 커스텀 이벤트 발생 (다른 컴포넌트에서 탭 변경 감지 가능)
    console.log('[useTabNavigation] 📣 tabChanged 이벤트 dispatch:', { pgmId: currentPgm, tabId });
    window.dispatchEvent(
      new CustomEvent('tabChanged', {
        detail: { pgmId: currentPgm, tabId, tabLabel: resolveTabLabel(selectedTab) },
      }),
    );
  };

  // iframe에 탭 변경 메시지 전송
  const sendTabChangeToIframes = (tabId: number, tabLabel: string, pgmId: string) => {
    const iframes = document.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      try {
        iframe.contentWindow?.postMessage(
          {
            type: 'TAB_CHANGED',
            data: {
              tabId,
              tabLabel,
              pgmId,
              url: window.location.href,
            },
          },
          '*',
        );
      } catch (error) {
        console.warn('iframe에 메시지 전송 실패:', error);
      }
    });
  };

  // 탭 상태 강제 초기화 함수 (사이드바 네비게이션용)
  const resetTabState = () => {
    console.log('[useTabNavigation] 🔄 resetTabState: 탭 상태 초기화');
    activeTab.value = 0;
    pendingTabChange.value = null;
  };

  // 초기화 함수
  const initializeTab = () => {
    const menuItem = getCurrentMenuItem();
    console.log('[useTabNavigation] 🔍 initializeTab:', {
      pgmId: menuItem?.pgmId,
      currentMenuPgmId: currentMenuPgmId.value,
      currentPath: route.path,
      activeTab: activeTab.value,
      isUserTabChange
    });

    if (menuItem && menuItem.tabs && menuItem.tabs.length > 0) {
      const currentPath = normalizeMenuRoutePath(route.path);

      // ★ 최우선: 메뉴가 변경되었는지 먼저 확인 (pgmId 기준)
      const menuChanged = currentMenuPgmId.value !== menuItem.pgmId;

      // 사용자가 탭을 클릭 직후: 선택한 탭 path와 URL이 같을 때만 스킵
      // (예: 보드 없을 때 /task/home 진입 → 곧바로 /task/collaboration 리다이렉트되면 URL과 activeTab이 어긋남 → 보드 탭으로 맞춰야 함)
      if (!menuChanged && isUserTabChange) {
        const selectedTabPath = menuItem.tabs[activeTab.value]?.path;
        if (selectedTabPath && currentPath === normalizeMenuRoutePath(selectedTabPath)) {
          console.log('[useTabNavigation] ⏭️ 사용자 탭 변경 중 - initializeTab 스킵');
          currentMenuPgmId.value = menuItem.pgmId;
          return;
        }
      }

      if (menuChanged) {
        console.log('[useTabNavigation] 🔄 메뉴 변경됨 → 첫 탭 우선');
        currentMenuPgmId.value = menuItem.pgmId;

        // URL이 특정 탭 path와 정확히 일치하면(북마크/직접 진입) 그 탭 유지
        const deepLinkTabIndex = menuItem.tabs.findIndex(
          tab => tab.path && currentPath === normalizeMenuRoutePath(tab.path),
        );
        if (deepLinkTabIndex >= 0) {
          activeTab.value = deepLinkTabIndex;
          pendingTabChange.value = null;
          return;
        }

        // 좌측 메뉴 등으로 진입: 항상 첫 번째 탭
        activeTab.value = 0;
        pendingTabChange.value = null;

        const firstTabPath = menuItem.tabs[0]?.path;
        if (firstTabPath && currentPath !== normalizeMenuRoutePath(firstTabPath)) {
          const targetPath = normalizeMenuRoutePath(firstTabPath);
          console.log('[useTabNavigation] 🔄 첫 번째 탭 경로로 리다이렉트:', targetPath);
          scheduleFirstTabRedirect(router, targetPath);
        }
        return;
      }

      // 같은 메뉴 내에서: 현재 경로가 탭 경로와 일치하면 해당 탭 사용
      const matchingTabIndex = menuItem.tabs.findIndex(
        tab => tab.path && currentPath === normalizeMenuRoutePath(tab.path),
      );

      if (matchingTabIndex >= 0) {
        activeTab.value = matchingTabIndex;
        return;
      }

      // 우선순위 3: 쿼리 파라미터에서 탭 정보 확인
      const urlParams = new URLSearchParams(window.location.search);
      const tab = urlParams.get('tab');
      let tabFromQuery = -1;

      if (tab) {
        const tabIndex = parseInt(tab, 10);
        if (!isNaN(tabIndex)) {
          tabFromQuery = tabIndex;
        }
      }

      // 쿼리에서 유효한 탭을 찾았으면 설정
      if (tabFromQuery >= 0 && tabFromQuery < menuItem.tabs.length) {
        activeTab.value = tabFromQuery;
      } else if (
        menuItem.defaultTab !== undefined &&
        menuItem.defaultTab >= 0 &&
        menuItem.defaultTab < menuItem.tabs.length
      ) {
        // 일치하는 탭이 없으면 defaultTab으로 설정
        activeTab.value = menuItem.defaultTab;
      }
    } else {
      // 탭이 없는 메뉴로 이동한 경우 currentMenuPgmId 초기화
      if (menuItem) {
        currentMenuPgmId.value = menuItem.pgmId || '';
      }
    }
  };

  // 라우트 변경 시 탭 초기화
  watch(
    () => route.path,
    () => {
      initializeTab();
    },
    { immediate: true },
  );

  // menuSections가 로드되면 탭 초기화 (새로고침 시 대응)
  watch(
    () => menuSections.value.length,
    newLength => {
      if (newLength > 0) {
        initializeTab();
      }
    },
  );

  // 브라우저 뒤로가기/앞으로가기 대응
  const handlePopstate = () => {
    parseTabFromQuery();
  };

  // 컴포넌트 마운트 시 popstate 이벤트 리스너 등록
  if (process.client) {
    window.addEventListener('popstate', handlePopstate);
  }

  // pendingTabChange 초기화 함수
  const clearPendingTabChange = () => {
    pendingTabChange.value = null;
  };

  // 상단 탭 메뉴 접근 로그 (레거시 logTabMenuAccess)
  // pgmId를 넘기면 클릭한 메뉴 기준으로 기록한다. 탭이 없는 메뉴는 menuTabId를 빈 값으로 넘긴다.
  const logTabMenuAccess = (menuTabId: string | number = '', pgmId?: string) => {
    const params = new URLSearchParams();
    params.append('menuTabId', String(menuTabId ?? ''));
    params.append('pgmId', pgmId != null ? String(pgmId) : currentPgmId.value);

    useApi('/api/common/insertTabMenuAccessLog.do', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
      skipLoading: true,
      skipErrorToast: true,
    }).catch(error => {
      console.error('탭 메뉴 접근 로그 저장 실패:', error);
    });
  };

  return {
    activeTab,
    currentTabs,
    currentPgmId,
    logTabMenuAccess,
    handleTabChange,
    initializeTab,
    getCurrentMenuItem,
    parseTabFromQuery,
    updateQuery,
    pendingTabChange,
    clearPendingTabChange,
    resetTabState,
  };
};
